import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { Typography, useTheme } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { enqueueSnackbar } from 'notistack';
import OperationLogDialog from './OperationLogDialog';
import SelfCheckResultDialog from './SelfCheckResultDialog';
import { MESSAGES } from '../common/messages';

import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';

const DetailTrainingResult = (props) => {
  const { typography, palette } = useTheme();
  const [trainingResult, setTrainingResult] = useState([]);
  const [trainerChecks, setTrainerChecks] = useState([]);
  const [comment, setComment] = useState('');
  const [isTrainerCheckDone, setIsTrainerCheckDone] = useState(false);
  const [openOperationLogDialog, setOpenOperationLogDialog] = useState(false);
  const [openSelfCheckResultDialog, setOpenSelfCheckResultDialog] = useState(false);

  const styles = {
    headlineText: {
      ...typography.caption13bold,
      color: 'secondary.main',
    },
    confirmButton: {
      ...typography.body14bold,
      width: '154px',
      height: '44px',
      marginTop: '8px',
      color: 'primary.main',
      border: '1px solid ' + palette.primary.main,
    },
    checkboxText: {
      width: '24px',
      height: '24px',
      color: 'primary.main',
    },
    trainerCheckRegistrationButton: {
      ...typography.label14bold,
      width: '232px',
      height: '44px',
      borderRadius: '100px',
      color: 'white',
      backgroundColor: 'primary.light',
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get('/v1/training-results/' + props.selectedTrainingResultId);
        setTrainingResult(data);
        setTrainerChecks(data.trainerCheckResult);
        setComment(data.trainerComment || ''); // TextFieldエラー防止のため値がnullの場合は空文字で初期化
        setIsTrainerCheckDone(!!data.trainerName);
      } catch (err) { }
    };
    fetchData();
  }, []);

  const handleClose = () => {
    setTrainingResult('');
    props.handleMoveToTrainingResult();
  };

  const handleSubmit = async () => {
    try {
      const trainerCheckData = {
        trainingResultId: props.selectedTrainingResultId,
        trainerChecks:
          trainerChecks.map((item) => (
            {
              itemId: item._id,
              isChecked: item.isChecked,
            }
          )),
        comment: comment,
      };
      await axios.post('/v1/trainer-check-results', trainerCheckData);
      handleClose();
      enqueueSnackbar(MESSAGES.TRAINER_CHECK_RESULT_CREATED);
    } catch (err) { }
  };

  const toggleChecked = (checked, itemId) => {
    const copied = structuredClone(trainerChecks);
    const targetIndex = copied.findIndex((item) => item._id === itemId);
    copied[targetIndex].isChecked = checked;
    setTrainerChecks(copied);
  };

  const ConfirmButton = (props) => {
    return (
      <Button variant="outlined"
        sx={styles.confirmButton}
        startIcon={<ListAltIcon />}
        onClick={() => {
          if (props.confirmItems.itemCount) {
            setOpenSelfCheckResultDialog(true);
          } else {
            setOpenOperationLogDialog(true);
          }
        }}
      >
        詳細を確認する
      </Button>
    );
  };

  const TrainerNameInformation = (props) => {
    const trainerName = props.trainerName;
    if (!trainerName) {
      return <Typography sx={{ color: 'error.main' }}>未入力</Typography>;
    } else {
      return <Typography>{trainerName}</Typography>;
    }
  };

  const SelfCheckResultInformation = (props) => {
    const selfCheckResult = props.selfCheckResult;
    if (!selfCheckResult) {
      return <Typography sx={typography.body14}>-</Typography>;
    } else {
      const style = selfCheckResult.correctAnswerCount === selfCheckResult.itemCount ?
        typography.body14 : { ...typography.body14, color: 'error.main' };
      return (
        <>
          <Typography sx={style}>
            {`正答数 ${selfCheckResult.correctAnswerCount}/${selfCheckResult.itemCount}点`}
          </Typography>
          <ConfirmButton confirmItems={selfCheckResult} />
        </>
      );
    }
  };

  const TrainerCheckResultInformation = (props) => {
    const trainerCheckResult = props.trainerCheckResult;
    if (trainerCheckResult) {
      return (
        trainerCheckResult.map((tcr) => (
          <Stack direction='row' marginTop='16px' key={tcr.serialNumber}>
            <Checkbox
              checked={tcr.isChecked} sx={styles.checkboxText}
              onChange={(_, checked) => toggleChecked(checked, tcr._id)}
              disabled={isTrainerCheckDone}
            />
            <Typography sx={{ marginLeft: '8px' }}>{tcr.text}</Typography>
          </Stack>
        ))
      );
    }
  };

  return (
    <Container sx={{ width: '100%', padding: '16px 24px' }}>
      <Paper sx={{ padding: '24px' }}>
        <Stack direction='row' marginBottom='36px'>
          <Stack sx={{ minWidth: '160px' }}>
            <FormLabel sx={styles.headlineText}>受講アカウント</FormLabel>
            <Typography>
              {`${trainingResult.driverEmployeeNumber} ${trainingResult.driverName}`}
            </Typography>
          </Stack>
          <Stack sx={{ marginLeft: '36px' }}>
            <FormLabel sx={styles.headlineText}>日時</FormLabel>
            <Typography>
              {`${dayjs(trainingResult.startedAt).format('YYYY年M月D日　HH:mm')} ~ `}
              {`${dayjs(trainingResult.finishedAt).format('HH:mm')}`}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' marginBottom='36px'>
          <Stack sx={{ minWidth: '160px' }}>
            <FormLabel sx={styles.headlineText}>指導者確認</FormLabel>
            <TrainerNameInformation trainerName={trainingResult.trainerName} />
          </Stack>
          <Stack sx={{ minWidth: '251px', marginLeft: '36px' }}>
            <FormLabel sx={styles.headlineText}>操作ログ</FormLabel>
            <Typography sx={typography.body14}>{`送信通告数 ${trainingResult.noticeCount}件`}</Typography>
            <ConfirmButton confirmItems={trainingResult.operationLog} />
            <OperationLogDialog
              open={openOperationLogDialog}
              setOpen={setOpenOperationLogDialog}
              operationLog={trainingResult.operationLog}
            />
          </Stack>
          <Stack sx={{ marginLeft: '36px' }}>
            <FormLabel sx={styles.headlineText}>受講者セルフチェック</FormLabel>
            <SelfCheckResultInformation selfCheckResult={trainingResult.selfCheckResult} />
            <SelfCheckResultDialog
              open={openSelfCheckResultDialog}
              setOpen={setOpenSelfCheckResultDialog}
              selfCheckResult={trainingResult.selfCheckResult}
            />
          </Stack>
        </Stack>
        <Stack marginBottom='36px'>
          <FormLabel sx={styles.headlineText}>指導者チェック</FormLabel>
          <TrainerCheckResultInformation
            trainerCheckResult={trainerChecks}
            trainerName={trainingResult.trainerName}
          />
        </Stack>
        <Stack marginBottom='36px' sx={{ '& .MuiInputBase-root': { padding: '0' } }}>
          <FormLabel sx={styles.headlineText}>所見</FormLabel>
          <TextField placeholder='ここに所見を入力します。' multiline maxRows={7}
            inputProps={{
              style: { width: '100%', height: '160px', margin: '8px' },
              readOnly: isTrainerCheckDone,
            }}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
        <Stack sx={{ alignItems: 'center' }}>
          <Button variant='contained' onClick={() => handleSubmit()}
            sx={styles.trainerCheckRegistrationButton}
            disabled={isTrainerCheckDone}>
            登録
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default DetailTrainingResult;

DetailTrainingResult.propTypes = {
  selectedTrainingResultId: PropTypes.string,
  handleMoveToTrainingResult: PropTypes.func,
  selfCheckResult: PropTypes.object,
  trainerCheckResult: PropTypes.object,
  trainerName: PropTypes.string,
  confirmItems: PropTypes.object,
};
